// Helper function (can be outside the class or a static method if preferred)
const isVisible = (element) =>
    element.offsetWidth || element.offsetHeight || element.getClientRects().length;
  
  // Generic focusable elements string selector
const FOCUSABLE_EL_STRING =
    '[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable], audio[controls], video[controls], summary';
  
export class Modal {
    constructor(element) {
        this.element = element;
        this.triggers = document.querySelectorAll(
            `[aria-controls="${this.element.getAttribute('id')}"]`
        );
        this.firstFocusable = null;
        this.lastFocusable = null;
        this.moveFocusEl = null; // focus will be moved to this element when modal is open
        this.modalFocus = this.element.getAttribute('data-modal-first-focus')
            ? this.element.querySelector(
                this.element.getAttribute('data-modal-first-focus')
            )
            : null;
        this.selectedTrigger = null;
        this.preventScrollEl = this.getPreventScrollEl();
        this.showClass = 'modal--is-visible';
    
        // Bind methods to ensure 'this' context is correct
        this.handleTriggerClick = this.handleTriggerClick.bind(this);
        this.handleOpenModalEvent = this.handleOpenModalEvent.bind(this);
        this.handleCloseModalEvent = this.handleCloseModalEvent.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
    
        this.initModal();
    }
  
    getPreventScrollEl() {
        const querySelector = this.element.getAttribute('data-modal-prevent-scroll');
        return querySelector ? document.querySelector(querySelector) : null;
    }
  
    initModal() {
        // Open modal when clicking on trigger buttons
        if (this.triggers) {
            this.triggers.forEach((trigger) => {
                trigger.addEventListener('click', this.handleTriggerClick);
            });
        }
    
        // Listen to custom openModal event
        this.element.addEventListener('openModal', this.handleOpenModalEvent);
    
        // Listen to custom closeModal event
        this.element.addEventListener('closeModal', this.handleCloseModalEvent);
    
        // If modal is open by default -> initialise modal events
        if (this.element.classList.contains(this.showClass)) {
            this.initModalEvents();
        }
    }
  
    handleTriggerClick(event) {
        event.preventDefault();
        if (this.element.classList.contains(this.showClass)) {
            this.closeModal();
            return;
        }
        this.selectedTrigger = event.currentTarget;
        this.showModal();
        this.initModalEvents();
    }
  
    handleOpenModalEvent(event) {
        if (event.detail) this.selectedTrigger = event.detail;
        this.showModal();
        this.initModalEvents();
    }
  
    handleCloseModalEvent(event) {
        if (event.detail) this.selectedTrigger = event.detail;
        this.closeModal();
    }
  
    showModal() {
        this.element.classList.add(this.showClass);
        this.getFocusableElements();
        if (this.moveFocusEl) {
            this.moveFocusEl.focus();
            // Wait for the end of transitions before moving focus
            this.element.addEventListener('transitionend', this.handleTransitionEnd);
        }
        this.emitModalEvents('modalIsOpen');
        // Change the overflow of the preventScrollEl
        if (this.preventScrollEl) this.preventScrollEl.style.overflow = 'hidden';
    }
  
    handleTransitionEnd() {
        if (this.moveFocusEl) {
            this.moveFocusEl.focus();
        }
        this.element.removeEventListener('transitionend', this.handleTransitionEnd);
    }
  
    closeModal() {
        if (!this.element.classList.contains(this.showClass)) return;
        this.element.classList.remove(this.showClass);
        this.firstFocusable = null;
        this.lastFocusable = null;
        this.moveFocusEl = null;
        if (this.selectedTrigger) this.selectedTrigger.focus();
        // Remove listeners
        this.cancelModalEvents();
        this.emitModalEvents('modalIsClose');
        // Change the overflow of the preventScrollEl
        if (this.preventScrollEl) this.preventScrollEl.style.overflow = '';
    }
  
    initModalEvents() {
        // Add event listeners
        this.element.addEventListener('keydown', this.handleKeyDown);
        this.element.addEventListener('click', this.handleClick);
    }
  
    cancelModalEvents() {
        // Remove event listeners
        this.element.removeEventListener('keydown', this.handleKeyDown);
        this.element.removeEventListener('click', this.handleClick);
    }
  
    // Unified event handler is less common with class methods, direct binding is often clearer.
    // If you prefer the handleEvent pattern, you can keep it and bind `this.element.addEventListener('keydown', this);`
    // For this refactor, I'm using separate bound methods for clarity.
  
    handleKeyDown(event) {
        if (event.key === 'Tab') {
            // Trap focus inside modal
            this.trapFocus(event);
        } else if (
            event.key === 'Enter' &&
            event.target.closest('.js-modal__close')
        ) {
            event.preventDefault();
            this.closeModal(); // Close modal when pressing Enter on close button
        } else if (event.key === 'Escape') {
            // Added Escape key handling directly here for modals that are open
            this.closeModal();
        }
    }
  
    handleClick(event) {
        // Close modal when clicking on close button or modal bg layer
        if (
            !event.target.closest('.js-modal__close') &&
            !event.target.classList.contains('js-modal') // Assuming 'js-modal' is the overlay
        ) {
            return;
        }
        event.preventDefault();
        this.closeModal();
    }
  
    trapFocus(event) {
        if (this.firstFocusable === document.activeElement && event.shiftKey) {
            // On Shift+Tab -> focus last focusable element when focus moves out of modal
            event.preventDefault();
            this.lastFocusable.focus();
        }
        if (this.lastFocusable === document.activeElement && !event.shiftKey) {
            // On Tab -> focus first focusable element when focus moves out of modal
            event.preventDefault();
            this.firstFocusable.focus();
        }
    }
  
    getFocusableElements() {
        // Get all focusable elements inside the modal
        const allFocusable = this.element.querySelectorAll(FOCUSABLE_EL_STRING);
        this.getFirstVisible(allFocusable);
        this.getLastVisible(allFocusable);
        this.determineFirstFocus(); // Renamed for clarity
    }
  
    getFirstVisible(elements) {
        // Get first visible focusable element inside the modal
        for (const element of elements) {
            if (isVisible(element)) {
            this.firstFocusable = element;
            break;
            }
        }
    }
  
    getLastVisible(elements) {
        // Get last visible focusable element inside the modal
        for (let i = elements.length - 1; i >= 0; i--) {
            if (isVisible(elements[i])) {
            this.lastFocusable = elements[i];
            break;
            }
        }
    }
  
    determineFirstFocus() { // Renamed from getFirstFocusable
        if (!this.modalFocus || !Element.prototype.matches) {
            this.moveFocusEl = this.firstFocusable;
            return;
        }
        const containerIsFocusable = this.modalFocus.matches(FOCUSABLE_EL_STRING);
        if (containerIsFocusable) {
            this.moveFocusEl = this.modalFocus;
        } else {
            this.moveFocusEl = null; // Reset before searching
            const elements = this.modalFocus.querySelectorAll(FOCUSABLE_EL_STRING);
            for (const element of elements) {
            if (isVisible(element)) {
                this.moveFocusEl = element;
                break;
            }
            }
            if (!this.moveFocusEl) this.moveFocusEl = this.firstFocusable;
        }
    }
  
    emitModalEvents(eventName) {
        const event = new CustomEvent(eventName, { detail: this.selectedTrigger });
        this.element.dispatchEvent(event);
    }
}
  